/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    NodeTag,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { TileListLinks } from './components';
import { IHeadingData, IExtTileListItemProps, IExtTileListItemResources } from './ext-tile-list-item.props.autogenerated';
import { IPlayerData, IPlayerOptions, IPlayerResources, IVideoMetadata, Player } from '@msdyn365-commerce-modules/video-player-utilities';
import { ImageFetchPriority } from '@msdyn365-commerce/core';

/**
 * Tile-list-item view props.
 */
export interface ITileListItemViewProps extends IExtTileListItemProps<{}> {
    heading?: React.ReactNode;
    paragraph?: React.ReactNode;
    links?: React.ReactNode;
    backgroundImage?: React.ReactNode;
    backgroundVideo?: React.ReactNode;
    isMobileDevice?: boolean;
    hoverBackgroundImage?: React.ReactNode;
    hoverBackgroundVideo?: React.ReactNode;
    hoverBackgroundVideoConfig?: IExtTileListItemResources;
    thumbnailImage?: React.ReactNode;
    tileListItem: IModuleProps;
    tileListContainer: INodeProps;
    tileListImageContainer: INodeProps;
    tileListThumbnailImageContainer: INodeProps;
    tileListHeadingContainer: INodeProps;
}

/**
 *
 * TileListItem component.
 * @extends {React.PureComponent<IExtTileListItemProps<{}>>}
 */
export class TileListItem extends React.PureComponent<IExtTileListItemProps<{}>> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );
    private readonly isMobileDevice: boolean;

    public constructor(props: IExtTileListItemProps<{}>) {
        super(props);
        this.isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';
    }

    public render(): JSX.Element {
        const {
            heading,
            paragraph,
            backgroundImage,
            backgroundVideo,
            hoverBackgroundImage,
            hoverBackgroundVideo,
            thumbnailImage,
            redirectionUrl,
            links
        } = this.props.config;
        const tileListLinks = links && ArrayExtensions.hasElements(links) && (
            <TileListLinks
                links={links}
                onTextChange={this.handleLinkTextChange}
                requestContext={this.props.context.request}
                telemetryContent={this._telemetryContent}
            />
        );
        const tileListLinkContainer = {
            tag: 'a' as NodeTag,
            className: 'ms-tile-list-container',
            role: 'link',
            href: redirectionUrl?.linkUrl.destinationUrl,
            'aria-label': redirectionUrl?.ariaLabel,
            target: redirectionUrl?.openInNewTab ? '_blank' : undefined
        };

        const tileListButtonContainer = {
            tag: 'button' as NodeTag,
            className: 'ms-tile-list-container'
        };

        const viewProps: ITileListItemViewProps = {
            ...this.props,
            heading: this._createHeading(heading),
            paragraph: this._createParagraph(paragraph),
            backgroundVideo: this._renderVideo(backgroundVideo),
            backgroundImage: this._renderBackgroundImage(backgroundImage),
            hoverBackgroundImage: this._renderHoverBackgroundImage(hoverBackgroundImage),
            hoverBackgroundVideo: this._renderVideo(hoverBackgroundVideo),
            thumbnailImage: this._renderThumbnailImage(thumbnailImage),
            isMobileDevice: this.isMobileDevice,
            links: tileListLinks,
            tileListItem: {
                moduleProps: this.props,
                className: 'ms-tile-list-item'
            },
            tileListContainer: redirectionUrl ? tileListLinkContainer : tileListButtonContainer,
            tileListImageContainer: {
                className: 'ms-tile-list__image'
            },
            tileListThumbnailImageContainer: {
                className: 'ms-tile-list__thumbnail_image',
                'aria-hidden': 'true'
            },
            tileListHeadingContainer: {
                className: 'ms-tile-list__heading'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles heading content change event.
     * @param event - Content editable event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param event - Content editable event.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.links?.[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    };

    private _createParagraph(text?: Msdyn365.RichText): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.RichTextComponent
                text={text}
                className='ms-tile-list__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _createHeading(heading?: IHeadingData): React.ReactNode | null {
        if (!heading) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }

        return (
            <Msdyn365.Text
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                className='ms-tile-list__title'
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _renderBackgroundImage(item?: Msdyn365.IImageData, isAriaHiddenAttribute?: boolean): React.ReactNode | null {
        // If image is null, we want to render the placeholder SVG - by passing image data with empty src

        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=377&h=515&m=6', w: 377, h: 515 },
                sm: { q: 'w=377&h=515&m=6', w: 377, h: 515 },
                md: { q: 'w=362&h=515&m=6', w: 362, h: 515 },
                lg: { q: 'w=362&h=515&m=6', w: 362, h: 515 },
                xl: { q: 'w=362&h=515&m=6', w: 362, h: 515 }
            },
            lazyload: this.props.config.backgroundImage?.imageSettings?.lazyload
        };
        const verifiedImageSettings = item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.backgroundImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
                fetchPriority={this.props.config.fetchPriorityHigh ? ImageFetchPriority.High : undefined}
            />
        );
    }

    private _renderVideo(item?: Msdyn365.IVideoData): React.ReactNode | null {
        if (!item) {
            return null;
        }
        const videoMetaData: IVideoMetadata = {};
        videoMetaData.videoId = item.id;
        videoMetaData.title = item.title;
        videoMetaData.duration = item.playTime;

        if (item.thumbnail) {
            videoMetaData.posterframeUrl = item.thumbnail.src;
        }

        if (item._links?.binaryReferences) {
            videoMetaData.videoBinaryReferences = item._links.binaryReferences;
        }

        const options = {
            ...this.props.resources,
            loop: true,
            autoplay: true,
            mute: true,
            playPauseTrigger: false,
            controls: false,
            playbackSpeed: false,
            download: false,
            playFullScreen: false,
            isMobileDevice: this.isMobileDevice
        };

        const videoPlayerData: IPlayerData = { options: options as IPlayerOptions, metaData: videoMetaData };
        const playerResources: IPlayerResources = {
            playAriaLabel: options.playLabel,
            pauseAriaLabel: options.pauseLabel,
            playingAriaLabel: options.playingLabel,
            pausedAriaLabel: options.pausedLabel,
            muteAriaLabel: options.muteLabel,
            unMuteAriaLabel: options.unMuteLabel,
            fullScreenAriaLabel: options.fullScreenLabel,
            exitFullScreenAriaLabel: options.exitFullScreenLabel,
            seekBarLabel: options.seekBarLabel,
            videoTimeDurationLabel: options.videoTimeDurationLabel,
            closedCaptionLabel: options.closedCaptionLabel
        };

        return (
            <Msdyn365.Video className='ms-tile-list__video' editProps={{ key: item, requestContext: this.props.context.request }}>
                <Player
                    playerData={videoPlayerData}
                    playerResources={playerResources}
                    telemetryContent={this._telemetryContent}
                    isEditor={this.props.context.request.params.isEditor}
                />
            </Msdyn365.Video>
        );
    }

    private _renderHoverBackgroundImage(item?: Msdyn365.IImageData, isAriaHiddenAttribute?: boolean): React.ReactNode | null {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                sm: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                md: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                lg: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                xl: { q: 'w=315&h=412&m=6', w: 315, h: 412 }
            },
            lazyload: this.props.config.backgroundImage?.imageSettings?.lazyload
        };
        const verifiedImageSettings = item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.backgroundImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
                fetchPriority={this.props.config.fetchPriorityHigh ? ImageFetchPriority.High : undefined}
            />
        );
    }

    private _renderThumbnailImage(
        item?: Msdyn365.IImageData,
        itemImageSettings?: Msdyn365.IImageSettings,
        isAriaHiddenAttribute?: boolean
    ): React.ReactNode | null {
        if (!item) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(item.src)) {
            return null;
        }

        return (
            <Msdyn365.Image
                {...item}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={itemImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.thumbnailImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    // eslint-disable-next-line max-len -- Auto-suppressed.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Auto-suppressed.
                    layout: (this.props.config as any).msdyn365__moduleLayout
                }}
                fetchPriority={this.props.config.fetchPriorityHigh ? ImageFetchPriority.High : undefined}
            />
        );
    }
}

export default TileListItem;
